import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatSocket } from '../contexts/ChatSocketContext';
import { apiService } from '../services/api';

export type ChatConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface ChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  recipientId: string;
  content: string;
  createdAt: number;
}

export interface ChatParticipant {
  id: string;
  name: string;
  instrument?: string;
}

export interface ChatConversation {
  id: string;
  participantId: string;
  participant?: ChatParticipant;
  messages: ChatMessage[];
  lastMessage?: ChatMessage;
  unreadCount: number;
}

interface ChatEventPayload {
  type?: unknown;
  chatId?: unknown;
  senderId?: unknown;
  recipientId?: unknown;
  content?: unknown;
  createdAt?: unknown;
  participants?: unknown;
  messageId?: unknown;
}

type ParsedChatSocketEvent =
  | {
      type: 'SEND_MESSAGE';
      message: ChatMessage;
      participantId: string;
    }
  | {
      type: 'MARK_AS_READ';
      chatId: string;
      senderId: string;
    };

const parseChatEvent = (raw: string, currentUserId: string): ParsedChatSocketEvent | null => {
  let payload: ChatEventPayload;
  try {
    payload = JSON.parse(raw) as ChatEventPayload;
  } catch (error) {
    return null;
  }

  if (!payload || typeof payload !== 'object') return null;

  const type = typeof payload.type === 'string' ? payload.type : '';

  if (type === 'MARK_AS_READ') {
    const chatId = typeof payload.chatId === 'string' ? payload.chatId : '';
    const senderId = typeof payload.senderId === 'string' ? payload.senderId : '';

    if (!chatId || !senderId) {
      return null;
    }

    return {
      type: 'MARK_AS_READ',
      chatId,
      senderId,
    };
  }

  if (type !== 'SEND_MESSAGE') {
    return null;
  }

  const chatId = typeof payload.chatId === 'string' ? payload.chatId : '';
  const senderId = typeof payload.senderId === 'string' ? payload.senderId : '';
  const recipientId = typeof payload.recipientId === 'string' ? payload.recipientId : '';
  const content = typeof payload.content === 'string' ? payload.content : '';
  const createdAtValue =
    typeof payload.createdAt === 'number'
      ? payload.createdAt
      : Number(payload.createdAt);

  if (!chatId || !senderId || !recipientId || !content || !Number.isFinite(createdAtValue)) {
    return null;
  }

  const participants = Array.isArray(payload.participants)
    ? payload.participants.filter((item): item is string => typeof item === 'string')
    : [];
  const participantId =
    participants.find((id) => id !== currentUserId) ||
    (senderId === currentUserId ? recipientId : senderId);

  const messageId =
    typeof payload.messageId === 'string' && payload.messageId.trim()
      ? payload.messageId
      : `${chatId}-${createdAtValue}-${senderId}`;

  const message: ChatMessage = {
    id: messageId,
    chatId,
    senderId,
    recipientId,
    content,
    createdAt: createdAtValue,
  };

  return { type: 'SEND_MESSAGE', message, participantId };
};

const getNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getUnreadCountForUser = (value: unknown, currentUserId: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const unreadValue = getNumericValue((value as Record<string, unknown>)[currentUserId]);
    return unreadValue ?? 0;
  }

  return 0;
};

const normalizeLastMessage = (
  value: any,
  chatId: string,
  participantId: string,
  currentUserId: string,
  fallbackTimestamp: number | null
): ChatMessage | undefined => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const content = typeof value.content === 'string' ? value.content : '';
    const createdAt = getNumericValue(value.createdAt) ?? fallbackTimestamp;
    const senderId = typeof value.senderId === 'string' ? value.senderId : '';

    if (content && createdAt !== null) {
      const resolvedSenderId = senderId || participantId || currentUserId;
      const resolvedRecipientId =
        resolvedSenderId === currentUserId ? participantId : currentUserId;

      return {
        id: `${chatId}-last`,
        chatId,
        senderId: resolvedSenderId,
        recipientId: resolvedRecipientId,
        content,
        createdAt,
      };
    }
  }

  if (typeof value === 'string' && value && fallbackTimestamp !== null) {
    return {
      id: `${chatId}-last`,
      chatId,
      senderId: participantId || currentUserId,
      recipientId: participantId ? currentUserId : '',
      content: value,
      createdAt: fallbackTimestamp,
    };
  }

  return undefined;
};

const normalizeMessageDocument = (value: any): ChatMessage | null => {
  const chatId = typeof value?.chatId === 'string' ? value.chatId : '';
  const senderId = typeof value?.senderId === 'string' ? value.senderId : '';
  const recipientId = typeof value?.recipientId === 'string' ? value.recipientId : '';
  const content = typeof value?.content === 'string' ? value.content : '';
  const createdAt = getNumericValue(value?.createdAt);
  const id = typeof value?._id === 'string' ? value._id : typeof value?.id === 'string' ? value.id : '';

  if (!chatId || !senderId || !content || createdAt === null) {
    return null;
  }

  return {
    id: id || `${chatId}-${createdAt}-${senderId}`,
    chatId,
    senderId,
    recipientId,
    content,
    createdAt,
  };
};

const normalizeChatDocument = (value: any, currentUserId: string): ChatConversation | null => {
  const id = typeof value?._id === 'string' ? value._id : typeof value?.id === 'string' ? value.id : '';
  if (!id) return null;
  const participants = Array.isArray(value?.participants)
    ? value.participants.filter((item: unknown): item is string => typeof item === 'string')
    : [];
  const participantId = participants.find((participant: string) => participant !== currentUserId) || '';
  const unreadCount = getUnreadCountForUser(value?.unreadCount, currentUserId);
  const lastMessageTimestamp = getNumericValue(value?.updatedAt) ?? getNumericValue(value?.createdAt);
  const lastMessage = normalizeLastMessage(
    value?.lastMessage,
    id,
    participantId,
    currentUserId,
    lastMessageTimestamp
  );

  return {
    id,
    participantId,
    participant: undefined,
    messages: [],
    lastMessage,
    unreadCount,
  };
};

export interface SendMessagePayload {
  chatId?: string;
  recipientId: string;
  content: string;
}

export interface UseChatResult {
  conversations: ChatConversation[];
  activeConversation: ChatConversation | null;
  activeChatId: string | null;
  status: ChatConnectionStatus;
  error: string | null;
  selectConversation: (chatId: string) => void;
  clearConversationSelection: () => void;
  sendMessage: (payload: SendMessagePayload) => boolean;
}

export const useChat = (currentUserId?: string | null): UseChatResult => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { status, error: socketError, lastEvent, unreadByChat, markChatAsReadLocally, sendJson } = useChatSocket();

  const activeChatIdRef = useRef<string | null>(null);
  const pendingProfilesRef = useRef<Set<string>>(new Set());
  const pendingMessagesRef = useRef<Set<string>>(new Set());

  const markConversationAsRead = useCallback(
    (chatId: string, participantId?: string) => {
      if (!currentUserId || !chatId) return false;

      return sendJson({
        type: 'MARK_AS_READ',
        chatId,
        senderId: currentUserId,
        recipientId: participantId ?? currentUserId,
      });
    },
    [currentUserId, sendJson]
  );

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  const updateConversation = useCallback(
    (chatId: string, updater: (conversation: ChatConversation) => ChatConversation) => {
      setConversations((prev) => {
        const index = prev.findIndex((conversation) => conversation.id === chatId);
        if (index === -1) return prev;
        const updated = updater(prev[index]);
        const next = [...prev];
        next[index] = updated;
        return next;
      });
    },
    []
  );

  const ensureParticipant = useCallback(
    (chatId: string, participantId: string) => {
      if (!participantId || pendingProfilesRef.current.has(participantId)) return;

      pendingProfilesRef.current.add(participantId);
      apiService
        .getUserById(participantId)
        .then((userData) => {
          const name =
            typeof userData?.name === 'string' && userData.name.trim().length > 0
              ? userData.name
              : typeof userData?.email === 'string'
                ? userData.email
                : participantId;
          const instrument =
            typeof userData?.instrument === 'string' && userData.instrument.trim().length > 0
              ? userData.instrument
              : undefined;

          updateConversation(chatId, (conversation) => ({
            ...conversation,
            participant: {
              id: participantId,
              name,
              instrument,
            },
          }));
        })
        .catch((err) => {
          console.error('Failed to load chat participant profile:', err);
        })
        .finally(() => {
          pendingProfilesRef.current.delete(participantId);
        });
    },
    [updateConversation]
  );

  const hydrateMessages = useCallback(
    (chatId: string) => {
      if (!chatId || pendingMessagesRef.current.has(chatId)) return;
      pendingMessagesRef.current.add(chatId);
      apiService
        .getMessagesForChat(chatId)
        .then((messages) => {
          const normalized = Array.isArray(messages)
            ? messages.map(normalizeMessageDocument).filter((item): item is ChatMessage => !!item)
            : [];
          updateConversation(chatId, (conversation) => {
            if (normalized.length === 0) {
              return {
                ...conversation,
                messages: [],
              };
            }
            const lastMessage = normalized[normalized.length - 1];
            return {
              ...conversation,
              messages: normalized,
              lastMessage,
            };
          });
        })
        .catch((err) => {
          console.error('Failed to load chat messages:', err);
        })
        .finally(() => {
          pendingMessagesRef.current.delete(chatId);
        });
    },
    [updateConversation]
  );

  const handleIncomingMessage = useCallback(
    (raw: string) => {
      if (!currentUserId) return;
      const parsed = parseChatEvent(raw, currentUserId);
      if (!parsed) return;

      if (parsed.type === 'MARK_AS_READ') {
        return;
      }

      const { message, participantId } = parsed;

      setConversations((prev) => {
        const next = [...prev];
        const index = next.findIndex((conversation) => conversation.id === message.chatId);

        if (index === -1) {
          next.push({
            id: message.chatId,
            participantId,
            participant: undefined,
            messages: [message],
            lastMessage: message,
            unreadCount: unreadByChat[message.chatId] ?? 0,
          });
        } else {
          const conversation = next[index];
          const alreadyPresent = conversation.messages.some((existing) => existing.id === message.id);
          const updatedMessages = alreadyPresent
            ? conversation.messages
            : [...conversation.messages, message];

          next[index] = {
            ...conversation,
            participantId: conversation.participantId || participantId,
            messages: updatedMessages,
            lastMessage: message,
            unreadCount: unreadByChat[message.chatId] ?? conversation.unreadCount,
          };
        }

        return next.sort(
          (a, b) => (b.lastMessage?.createdAt ?? 0) - (a.lastMessage?.createdAt ?? 0)
        );
      });

      if (!activeChatIdRef.current) {
        setActiveChatId(message.chatId);
      }

      ensureParticipant(message.chatId, participantId);
    },
    [currentUserId, ensureParticipant, unreadByChat]
  );

  useEffect(() => {
    if (!currentUserId) {
      setConversations([]);
      setActiveChatId(null);
      return;
    }

    let isMounted = true;
    const fetchAllChats = async () => {
      const pageSize = 50;
      const maxPages = 20;
      let offset = 0;
      let pagesFetched = 0;
      const allChats: any[] = [];

      while (pagesFetched < maxPages) {
        const page = await apiService.getChatsForUser(currentUserId, pageSize, offset);
        if (!Array.isArray(page) || page.length === 0) {
          break;
        }
        allChats.push(...page);
        if (page.length < pageSize) {
          break;
        }
        offset += pageSize;
        pagesFetched += 1;
      }

      return allChats;
    };

    fetchAllChats()
      .then((chats) => {
        if (!isMounted) return;
        const normalized = Array.isArray(chats)
          ? chats
              .map((chat) => normalizeChatDocument(chat, currentUserId))
              .filter((item): item is ChatConversation => !!item)
          : [];
        const uniqueNormalized = Array.from(
          new Map(normalized.map((conversation) => [conversation.id, conversation])).values()
        );

        setConversations((prev) => {
          const prevMap = new Map(prev.map((conversation) => [conversation.id, conversation]));
          const next = uniqueNormalized.map((conversation) => {
            const existing = prevMap.get(conversation.id);
            if (!existing) return conversation;
            return {
              ...conversation,
              participant: existing.participant ?? conversation.participant,
              messages: existing.messages.length > 0 ? existing.messages : conversation.messages,
              lastMessage: existing.lastMessage ?? conversation.lastMessage,
              unreadCount: conversation.unreadCount,
            };
          });
          const extras = prev.filter(
            (conversation) => !uniqueNormalized.some((item) => item.id === conversation.id)
          );
          const merged = [...next, ...extras];
          return merged.sort(
            (a, b) => (b.lastMessage?.createdAt ?? 0) - (a.lastMessage?.createdAt ?? 0)
          );
        });

        uniqueNormalized.forEach((conversation) => {
          if (conversation.participantId) {
            ensureParticipant(conversation.id, conversation.participantId);
          }
        });

        if (!activeChatIdRef.current && uniqueNormalized.length > 0) {
          setActiveChatId(uniqueNormalized[0].id);
        }
      })
      .catch((err) => {
        console.error('Failed to load chats:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [currentUserId, ensureParticipant]);

  useEffect(() => {
    if (!currentUserId) {
      setError(null);
      return;
    }
    setError(socketError);
  }, [currentUserId, socketError]);

  useEffect(() => {
    if (!currentUserId || !lastEvent) return;
    handleIncomingMessage(lastEvent.data);
  }, [currentUserId, handleIncomingMessage, lastEvent]);

  useEffect(() => {
    setConversations((prev) =>
      prev.map((conversation) => {
        const nextUnreadCount = unreadByChat[conversation.id] ?? 0;
        if (conversation.unreadCount === nextUnreadCount) {
          return conversation;
        }

        return {
          ...conversation,
          unreadCount: nextUnreadCount,
        };
      })
    );
  }, [unreadByChat]);

  const selectConversation = useCallback((chatId: string) => {
    setActiveChatId(chatId);
    markChatAsReadLocally(chatId);
    hydrateMessages(chatId);
  }, [hydrateMessages, markChatAsReadLocally]);

  const clearConversationSelection = useCallback(() => {
    setActiveChatId(null);
  }, []);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeChatId) ?? null,
    [conversations, activeChatId]
  );

  useEffect(() => {
    if (!activeConversation || activeConversation.unreadCount <= 0) return;

    markChatAsReadLocally(activeConversation.id);
    markConversationAsRead(activeConversation.id, activeConversation.participantId);
  }, [activeConversation, markChatAsReadLocally, markConversationAsRead]);

  useEffect(() => {
    if (!activeChatId) return;
    const conversation = conversations.find((item) => item.id === activeChatId);
    if (!conversation || conversation.messages.length > 0) return;
    hydrateMessages(activeChatId);
  }, [activeChatId, conversations, hydrateMessages]);

  const sendMessage = useCallback(
    (payload: SendMessagePayload) => {
      if (!currentUserId) return false;

      const trimmedContent = payload.content.trim();
      if (!trimmedContent) return false;

      const message = {
        type: 'SEND_MESSAGE',
        recipientId: payload.recipientId,
        senderId: currentUserId,
        content: trimmedContent,
        chatId: payload.chatId,
      };

      return sendJson(message);
    },
    [currentUserId, sendJson]
  );

  return {
    conversations,
    activeConversation,
    activeChatId,
    status,
    error,
    selectConversation,
    clearConversationSelection,
    sendMessage,
  };
};
